import { DataGridPro, GridColumns } from '@mui/x-data-grid-pro'
import { PrescriptionDto } from '@services/dtos'
import { useEffect, useMemo, useState } from 'react'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Box, Tooltip } from '@mui/material'
import { prescriptionsColumns } from '@utils/gridColumns'
import { PrescriptionDetailsDialog } from '../dialogs/PrescriptionDetailsDialog'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useSelector } from '@state/store'
import { getPrescriptions, setPrescriptionFilters } from '@state/reducers/prescriptionReducer'
import PrescriptionsTableToolbar from './Toolbars/PrescriptionsTableToolbar'

export default function PrescriptionsTable() {
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
  const { prescriptions, filters, totalCount, loading } = useSelector(({ prescription }) => ({
    filters: prescription.filters,
    prescriptions: prescription.prescriptions.datas,
    totalCount: prescription.prescriptions.totalCount,
    loading: prescription.loading,
  }))

  const selectedItem = useMemo(
    () => prescriptions.find(({ id }) => id === selectedItemId) ?? null,
    [prescriptions, selectedItemId]
  )

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(getPrescriptions())
    return () => {
      dispatch(setPrescriptionFilters({ page: 0 }))
    }
  }, [])
  useEffect(() => {
    dispatch(getPrescriptions())
  }, [filters, dispatch])

  const columns: GridColumns<PrescriptionDto> = useMemo(
    () => [
      {
        field: '__actions__',
        headerName: 'Actions',
        width: 75,
        sortable: false,
        renderCell: (params: any) => {
          const row = params.row as PrescriptionDto
          const actions = [
             {
              key: 'open',
              label: 'Ouvrir',
              icon: <OpenInNewIcon />,
              onClick: () => {
                setSelectedItemId(row.id)
                setDetailsDialogOpen(true)
              },
            }
          ]
          return (
            <div className="flex space-x-2">
              {actions.map((a) => (
                <Tooltip key={a.key} title={a.label}>
                  <div
                    className={'cursor-pointer p-1 hover:bg-gray-200 rounded'}
                    onClick={(e) => {
                      e.stopPropagation()
                      a.onClick()
                    }}
                  >
                    {a.icon}
                  </div>
                </Tooltip>
              ))}
            </div>
          )
        },
      },
      ...prescriptionsColumns
    ],
    [],
  )

  return (
    <Box sx={{ flex: 1, width: '100%', minHeight: { xs: 'calc(100vh - 160px)', md: 0 }, display: 'flex', flexDirection: 'column' }}>
      <DataGridPro
        density="standard"
        pagination
        pageSize={20}
        rowsPerPageOptions={[20]}
        paginationMode="server"
        components={{ Toolbar: PrescriptionsTableToolbar }}
        style={{
          height: '100%',
          width: '100%',
          backgroundColor: 'white',
          borderRadius: '15px 15px 25px 25px',
        }}
        onPageChange={(page) => {
          dispatch(setPrescriptionFilters({ page }))
          dispatch(getPrescriptions())
        }}
        sx={{
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#f8f8f8',
          },
        }}
        columns={columns}
        rows={prescriptions}
        rowCount={totalCount}
        loading={loading}
        disableSelectionOnClick
        disableMultipleSelection
      />

      {selectedItem && <PrescriptionDetailsDialog
        onClose={() => setDetailsDialogOpen(false)}
        open={detailsDialogOpen}
        prescription={selectedItem}
      />}
    </Box>
  )
}
